import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import subscriptionService from '../services/global/subscriptionService';
import useFeatureFlags from './useFeatureFlags';

// Com cadeiras (flag company_seats_billing, 2026-10-01) qualquer plano pago
// vira empresa; o Free não recebe o convite (inclui só o administrador).
const PAID_TIERS = ['sales_starter', 'sales_pro', 'sales_business'];

/**
 * useCreateCompanyNotification
 *
 * Decide se mostrar a notificação "Crie sua empresa" no inbox. Condições:
 * - User está logado E tem tier que contém "business"
 * - User NÃO está em uma empresa (company_id === null)
 * - Flag `infinitsales-company-prompt-dismissed` não está em localStorage
 *
 * Faz fetch leve do subscription status no mount; em caso de erro,
 * silenciosamente não mostra (fail-open: melhor não incomodar do que
 * mostrar notificação errada se a API estiver lenta).
 *
 * Espelha o padrão de `useAgentTrainingNotification.js` — mesmo delay
 * de 800ms para evitar flicker no mount.
 */
const DISMISSED_KEY = 'infinitsales-company-prompt-dismissed';
const SESSION_DELAY_MS = 800;

export function markCompanyPromptDismissed() {
  try {
    localStorage.setItem(DISMISSED_KEY, 'true');
  } catch {}
}

export default function useCreateCompanyNotification() {
  const { user, isInCompany } = useAuth();
  const { flags } = useFeatureFlags();
  // company_any_plan (14/10): mesmo convite sem a cobranca de cadeiras.
  const seatsBilling = !!flags.company_seats_billing || !!flags.company_any_plan;
  const [visible, setVisible] = useState(false);

  // user?.id (primitivo) nas deps: o objeto user ganha identidade nova a cada
  // refresh de perfil e re-disparava o fetch de subscription/status no mount
  // do inbox (parte dos 3x vistos em prod).
  const userId = user?.id;

  useEffect(() => {
    // Hard gates: precisa estar autenticado E não estar em empresa
    if (!userId || isInCompany) {
      setVisible(false);
      return;
    }
    // Dismissed previamente
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(DISMISSED_KEY) === 'true';
    } catch {}
    if (dismissed) return;

    let cancelled = false;
    // Buscar subscription status leve; só mostra se tier contém "business"
    subscriptionService
      .getSubscriptionStatus()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || res || {};
        const tier = (data.tier || '').toLowerCase();
        const elegivel = seatsBilling
          ? data.has_access !== false && PAID_TIERS.includes(tier)
          : tier.includes('business');
        if (!elegivel) return;
        // Pequeno delay para evitar flash inicial — mesmo padrão da notif de agente
        window.setTimeout(() => {
          if (!cancelled) setVisible(true);
        }, SESSION_DELAY_MS);
      })
      .catch(() => {
        // fail-open: silencia, não mostra
      });

    return () => {
      cancelled = true;
    };
  }, [userId, isInCompany, seatsBilling]);

  const dismiss = useCallback(() => {
    markCompanyPromptDismissed();
    setVisible(false);
  }, []);

  const markCreated = useCallback(() => {
    // Ao clicar CTA também marcamos dismissed — não vamos incomodar de novo
    markCompanyPromptDismissed();
    setVisible(false);
  }, []);

  return { visible, dismiss, markCreated };
}
